import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import styled from 'styled-components';

import {
  LeaveBalanceReportEmployee,
  LeaveBalanceReportResponse
} from 'domain-types';
import {
  AuthContext,
  baseUrl,
  isOrgFeatureEnabled,
  localizeLeaveType,
  useSpot
} from 'framework';
import { Line, Table, TitleContainer } from 'components';
import { StaffTabs } from './staff-tabs';

const EmployeeCard = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const EmployeeHeader = styled(Typography)`
  margin-bottom: ${p => p.theme.spacing(1)}px;
`;

const getErrorMessage = (t: (key: string) => string, err: unknown) => {
  if (Array.isArray(err)) {
    const error = err[0] as { response?: { error?: string } };
    if (error?.response?.error) {
      return error.response.error;
    }
  }

  return t('leaveBalancesLoadFailed');
};

export function LeaveBalancesReport() {
  const { organization } = useParams<{ organization: string }>();
  const { t } = useTranslation();
  const { getToken } = useContext(AuthContext);
  const { data, query } = useSpot();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState<LeaveBalanceReportResponse | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const leaveBalancesPdfUrl = useRef<string | null>(null);
  const org = data?.organizations?.[organization];

  useEffect(() => {
    if (!org) {
      query(`org/${organization}`, {}, ['organizations', organization]);
    }
  }, [org, organization, query]);

  useEffect(
    () => () => {
      if (leaveBalancesPdfUrl.current) {
        URL.revokeObjectURL(leaveBalancesPdfUrl.current);
        leaveBalancesPdfUrl.current = null;
      }
    },
    []
  );

  useEffect(() => {
    (async () => {
      if (!org || !isOrgFeatureEnabled(org, 'leave')) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setReport(null);
      try {
        const response = await fetch(
          `${baseUrl}/org/${organization}/reports/leave-balances`,
          {
            headers: {
              Authorization: `Bearer ${getToken().getJwtToken()}`
            }
          }
        );

        if (!response.ok) {
          let serverError = t('leaveBalancesLoadFailed');
          try {
            const payload = (await response.json()) as { error?: string };
            if (payload.error) {
              serverError = payload.error;
            }
          } catch {
            // No-op fallback to default translated message.
          }
          throw new Error(serverError);
        }

        const payload = (await response.json()) as LeaveBalanceReportResponse;
        setReport(payload);
      } catch (err) {
        setError(err instanceof Error ? err.message : getErrorMessage(t, err));
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, org, organization, t]);

  const onDownloadPdf = useCallback(async () => {
    setIsDownloadingPdf(true);
    setError('');
    try {
      const response = await fetch(
        `${baseUrl}/org/${organization}/reports/leave-balances/pdf`,
        {
          headers: {
            Authorization: `Bearer ${getToken().getJwtToken()}`
          }
        }
      );

      if (!response.ok) {
        let serverError = t('leaveBalancesPdfFailed');
        try {
          const payload = (await response.json()) as { error?: string };
          if (payload.error) {
            serverError = payload.error;
          }
        } catch {
          // No-op fallback to default translated message.
        }
        throw new Error(serverError);
      }

      const blob = await response.blob();
      const nextPdfUrl = URL.createObjectURL(blob);
      if (leaveBalancesPdfUrl.current) {
        URL.revokeObjectURL(leaveBalancesPdfUrl.current);
      }
      leaveBalancesPdfUrl.current = nextPdfUrl;

      const link = document.createElement('a');
      link.href = nextPdfUrl;
      link.download = 'leave-balances-report.pdf';
      link.click();
      link.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('leaveBalancesPdfFailed'));
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [getToken, organization, t]);

  const subtitle = report?.generatedAt
    ? `${t('generatedOn')}: ${new Date(report.generatedAt).toLocaleString()}`
    : '';
  const formatDays = (value: number) => `${value.toFixed(2)} ${t('days')}`;

  if (org && !isOrgFeatureEnabled(org, 'leave')) {
    return <Redirect to={`/${organization}`} />;
  }

  const renderEmployee = (employee: LeaveBalanceReportEmployee) => (
    <EmployeeCard key={employee.id}>
      <EmployeeHeader variant="subtitle1">
        {employee.name} ({employee.email})
      </EmployeeHeader>
      <Table
        headers={[
          t('type'),
          t('leaveTakenDays'),
          t('leaveRemainingDays'),
          t('leaveTotalDays')
        ]}
        rows={employee.leaveTypes.map((leaveType) => ({
          id: `${employee.id}-${leaveType.type}`,
          cells: [
            localizeLeaveType(t, leaveType.type),
            formatDays(leaveType.takenDays),
            formatDays(leaveType.remainingDays),
            formatDays(leaveType.totalDays)
          ]
        }))}
      />
    </EmployeeCard>
  );

  return (
    <StaffTabs organization={organization} selected="leaveBalances">
      <TitleContainer>
        <Box>
          {!!subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || isDownloadingPdf}
          onClick={onDownloadPdf}
        >
          {isDownloadingPdf ? t('downloadingPdf') : t('downloadPdf')}
        </Button>
      </TitleContainer>
      <Line />
      {loading && (
        <Box mt={2} display="flex" alignItems="center">
          <CircularProgress size={20} />
          <Box ml={1}>
            <Typography variant="body2">{t('loading')}</Typography>
          </Box>
        </Box>
      )}
      {!loading && !!error && (
        <Box mt={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {!loading && !error && !report?.employees?.length && (
        <Box mt={2}>
          <Typography>{t('leaveBalancesEmptyState')}</Typography>
        </Box>
      )}
      {!loading && !error && !!report?.employees?.length && (
        <Box>{report.employees.map(renderEmployee)}</Box>
      )}
    </StaffTabs>
  );
}
